import { canonicalizeRegions, type SelectionState } from './selection';
import type { RasterTileStore } from './tiles';
import type { Rect } from './geometry';

export type LuminosityFamily = 'highlights' | 'midtones' | 'shadows';
export type LuminosityLevel = 1 | 2 | 3;

function included(luminance: number, family: LuminosityFamily, level: LuminosityLevel): boolean {
  if (family === 'highlights') return luminance >= ([128, 170, 213][level - 1] ?? 128);
  if (family === 'shadows') return luminance <= ([127, 85, 42][level - 1] ?? 127);
  const [minimum, maximum] = ([[64, 191], [85, 170], [106, 149]][level - 1] ?? [64, 191]) as [number, number];
  return luminance >= minimum && luminance <= maximum;
}

export function luminosityMaskName(family: LuminosityFamily, level: LuminosityLevel): string {
  return `${family[0]?.toUpperCase() ?? ''}${family.slice(1)} ${level}`;
}

/** Converts an opaque raster's luminance band to compact, row-exact selection regions. */
export function luminosityMaskSelection(source: RasterTileStore, family: LuminosityFamily, level: LuminosityLevel): SelectionState {
  const regions: Rect[] = [];
  for (let y = 0; y < source.height; y += 1) {
    let start: number | null = null;
    for (let x = 0; x <= source.width; x += 1) {
      const tileX = Math.floor(x / source.tileSize);
      const tileY = Math.floor(y / source.tileSize);
      const pixels = x < source.width ? source.getTile(tileX, tileY) : undefined;
      const dimensions = x < source.width ? source.tileDimensions(tileX, tileY) : { width: 0 };
      const offset = ((y - tileY * source.tileSize) * dimensions.width + (x - tileX * source.tileSize)) * 4;
      const alpha = pixels?.[offset + 3] ?? 0;
      const luminance = (pixels?.[offset] ?? 0) * 0.2126 + (pixels?.[offset + 1] ?? 0) * 0.7152 + (pixels?.[offset + 2] ?? 0) * 0.0722;
      const selected = x < source.width && alpha > 0 && included(luminance, family, level);
      if (selected && start === null) start = x;
      if (!selected && start !== null) {
        regions.push({ x: start, y, width: x - start, height: 1 });
        start = null;
      }
    }
  }
  return { regions: canonicalizeRegions(regions), inverted: false };
}
